import { useEffect, useState } from 'react';
import { Code, Eye } from '../../icons';
import { fetchAttachmentText } from '../../lib/preview-doc';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * HTML 미리보기 (2026-08-14 2차) — 사용자 요구:
 *
 *   "html 파일은 미리보기 하면 소스를 보여주는게 아니라 화면으로 치환하여 보여줘."
 *
 * 종전 워크스페이스 패널은 html 을 마크다운과 같은 갈래로 묶어 **원문을 `<pre>` 로** 뿌렸다.
 * 채팅 쪽은 이미 격리 iframe 으로 그리고 있었는데(ChatDocPreviewPanel 의 HtmlContent) 이
 * 경로만 그것을 안 타고 있었다 — 같은 파일이 화면마다 다르게 보이는 상태 자체가 결함이다.
 *
 * ## 격리 규약 (채팅과 **같은 값**을 쓴다 — 한쪽만 느슨해지면 그쪽이 구멍이다)
 *
 *   - `sandbox=""` — 전부 차단. 스크립트·폼·팝업·top navigation 모두 안 돈다.
 *     빈 문자열이라 origin 도 부여되지 않아, 문서가 우리 쿠키·localStorage 에 못 닿는다.
 *   - 문서 앞에 CSP `<meta>` 를 덧붙인다 — 바깥으로 나가는 요청(추적 픽셀·폰트·iframe)을
 *     막는다. 다중 CSP 는 **교집합**이라 문서가 자기 CSP 로 이걸 느슨하게 만들 수 없다.
 *   - `referrerPolicy="no-referrer"`.
 *
 * 소스를 봐야 할 때가 있어(무엇이 들어 있는지 확인) 토글을 둔다. 기본은 **화면**이다.
 */

/** 채팅(ChatDocPreviewPanel)과 같은 값 — 한쪽만 바꾸지 말 것. */
const HTML_PREVIEW_CSP =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:; font-src data:; media-src data:">';

export function WorkspaceHtmlPreview({ fileId, name }: { fileId: string; name: string }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSource, setShowSource] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    setShowSource(false);
    fetchAttachmentText(fileId)
      .then((t) => { if (!cancelled) setText(t); })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'error');
      });
    return () => { cancelled = true; };
  }, [fileId]);

  if (error) return <div className="workspace-docpanel-status" role="status">{error}</div>;
  if (text === null) {
    return (
      <div className="workspace-docpanel-status" role="status">
        {tWorkspace('workspace.grid.loading')}
      </div>
    );
  }

  return (
    <div className="workspace-docpanel-html">
      <div className="workspace-docpanel-html-bar">
        <button
          type="button"
          className="workspace-docpanel-btn"
          onClick={() => setShowSource((v) => !v)}
        >
          {showSource
            ? <><Eye size={13} aria-hidden /> {tWorkspace('workspace.preview.html_rendered')}</>
            : <><Code size={13} aria-hidden /> {tWorkspace('workspace.preview.html_source')}</>}
        </button>
      </div>
      {showSource ? (
        <pre className="workspace-docpanel-text">{text}</pre>
      ) : (
        <iframe
          title={`${name} — ${tWorkspace('workspace.preview.html_aria')}`}
          className="workspace-docpanel-html-frame"
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={HTML_PREVIEW_CSP + text}
        />
      )}
    </div>
  );
}
